import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RefreshCw, FileText, Zap } from "lucide-react";
import { toast } from "sonner";

const statusMap = {
  routed: { label: "Routed", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  unmatched: { label: "No project match", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  no_folder: { label: "No folder mapped", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  box_not_connected: { label: "Storage not connected", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  storage_not_connected: { label: "Storage not connected", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  upload_failed: { label: "Upload failed", cls: "bg-rose-100 text-rose-700 border-rose-200" },
  unverified_sender: { label: "Unverified sender (blocked)", cls: "bg-rose-100 text-rose-700 border-rose-200" },
  attachment_too_large: { label: "Attachment too large", cls: "bg-rose-100 text-rose-700 border-rose-200" },
  sent: { label: "Sent", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  failed: { label: "Failed", cls: "bg-rose-100 text-rose-700 border-rose-200" },
};

function St({ s }) {
  const m = statusMap[s] || { label: s, cls: "bg-secondary text-secondary-foreground" };
  return <Badge variant="outline" className={m.cls}>{m.label}</Badge>;
}

export default function InboxRouting() {
  const [logs, setLogs] = useState([]);
  const [itbs, setItbs] = useState([]);
  const [conn, setConn] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = () => Promise.all([api.get("/routing-logs"), api.get("/itbs"), api.get("/email/connection")])
    .then(([l, i, c]) => { setLogs(l.data); setItbs(i.data); setConn(c.data?.connected ? c.data : null); });
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/inbox/sync");
      toast.success(`Synced: ${data.routed} PDF(s) routed from ${data.messages_with_pdfs} email(s)`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSyncing(false); }
  };

  return (
    <>
      <PageHeader title="Inbox Routing" description="Fetch new subcontractor emails and file PDFs into project folders"
        actions={<Button onClick={sync} disabled={syncing} data-testid="sync-inbox-btn"><RefreshCw className={`h-4 w-4 mr-1 ${syncing ? "animate-spin" : ""}`} />{syncing ? "Syncing…" : "Sync inbox"}</Button>} />
      <div className="p-8">
        <div className="mb-5 flex items-center gap-3 rounded-md border border-border bg-card px-4 py-3" data-testid="autopoll-banner">
          <div className="h-8 w-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
            <Zap className="h-4 w-4 text-primary" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold">Auto-routing is {conn ? "active" : "idle"}</p>
            <p className="text-xs text-muted-foreground">
              {conn
                ? <>Polling <span className="font-medium">{conn.email}</span> automatically. {conn.last_sync ? `Last sync ${new Date(conn.last_sync).toLocaleString()}` : "Waiting for first sync…"}</>
                : "Connect an inbox in Settings to start automatic PDF routing."}
            </p>
          </div>
          <Badge variant="outline" className={conn ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-secondary"}>
            {conn ? "Live" : "Off"}
          </Badge>
        </div>
        <Tabs defaultValue="routing">
          <TabsList>
            <TabsTrigger value="routing" data-testid="tab-routing">Routing Log</TabsTrigger>
            <TabsTrigger value="itbs" data-testid="tab-itbs">Sent ITBs</TabsTrigger>
          </TabsList>
          <TabsContent value="routing" className="mt-4">
            <Card className="border border-border overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>From</TableHead><TableHead>Subject</TableHead><TableHead>Attachment</TableHead><TableHead>Project</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {logs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">No routing activity yet. Click “Sync inbox”.</TableCell></TableRow>}
                  {logs.map((l) => (
                    <TableRow key={l.id} className="hover:bg-accent/50">
                      <TableCell className="text-sm">{l.from_name || l.from_addr}</TableCell>
                      <TableCell className="text-sm max-w-[240px] truncate">{l.subject}</TableCell>
                      <TableCell className="text-sm"><span className="flex items-center gap-1"><FileText className="h-3.5 w-3.5 text-primary" />{l.attachment_name}</span></TableCell>
                      <TableCell className="text-sm">{l.project_name || "—"}</TableCell>
                      <TableCell><St s={l.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
          <TabsContent value="itbs" className="mt-4">
            <Card className="border border-border overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Subject</TableHead><TableHead>Project</TableHead><TableHead>CSI</TableHead><TableHead>Recipients</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {itbs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">No ITBs sent yet.</TableCell></TableRow>}
                  {itbs.map((i) => (
                    <TableRow key={i.id} className="hover:bg-accent/50">
                      <TableCell className="text-sm max-w-[240px] truncate">{i.subject}</TableCell>
                      <TableCell className="text-sm">{i.project_name}</TableCell>
                      <TableCell className="text-sm font-mono">{i.csi_code}</TableCell>
                      <TableCell className="text-sm">{i.recipients?.length}</TableCell>
                      <TableCell><St s={i.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
